import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setAvailability, getProProfile } from "../api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = [
  "6:00 AM", "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
  "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM"
];

interface DaySchedule {
  available: boolean;
  start: string;
  end: string;
}

type WeekSchedule = Record<string, DaySchedule>;

const DEFAULT_SCHEDULE: WeekSchedule = {
  Monday:    { available: true,  start: "8:00 AM",  end: "6:00 PM" },
  Tuesday:   { available: true,  start: "8:00 AM",  end: "6:00 PM" },
  Wednesday: { available: true,  start: "8:00 AM",  end: "6:00 PM" },
  Thursday:  { available: true,  start: "8:00 AM",  end: "6:00 PM" },
  Friday:    { available: true,  start: "8:00 AM",  end: "6:00 PM" },
  Saturday:  { available: false, start: "9:00 AM",  end: "3:00 PM" },
  Sunday:    { available: false, start: "10:00 AM", end: "2:00 PM" },
};

export default function AvailabilityPage() {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState<WeekSchedule>(DEFAULT_SCHEDULE);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProProfile()
      .then((profile: any) => {
        if (profile?.availability) {
          setSchedule(profile.availability);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function toggleDay(day: string) {
    setSchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], available: !prev[day].available }
    }));
  }

  function updateTime(day: string, field: "start" | "end", value: string) {
    setSchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setAvailability(schedule);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save availability:", err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const availableDays = Object.values(schedule).filter(d => d.available).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-4 shadow-sm">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/home")} className="text-gray-400 text-sm">← Dashboard</button>
          <button onClick={handleSave} disabled={saving}
            className="bg-mouna-blue text-white rounded-full px-5 py-2 text-sm font-bold">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        <h1 className="font-display text-2xl font-bold text-navy mt-2">Availability</h1>
        <p className="text-xs text-gray-400 mt-1">Set your working hours for each day</p>
      </div>

      {success && (
        <div className="fixed top-4 left-4 right-4 bg-teal text-white text-sm rounded-xl p-4 font-bold text-center z-50 shadow-lg">
          Availability saved!
        </div>
      )}

      <div className="px-5 pt-4 pb-24">

        {/* Summary */}
        <div className="bg-gradient-to-br from-mouna-blue to-blue-900 rounded-2xl p-4 mb-5 text-white">
          <div className="text-xs text-blue-200 uppercase tracking-wider mb-1">Available Days</div>
          <div className="font-mono text-3xl font-medium">{availableDays} / 7</div>
          <div className="text-xs text-blue-200 mt-1">days per week</div>
        </div>

        {/* Day schedules */}
        {DAYS.map(day => (
          <div key={day} className={"bg-white rounded-2xl p-4 shadow-sm mb-3 transition-all " +
            (schedule[day].available ? "" : "opacity-60")}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleDay(day)}
                  className={"w-12 h-6 rounded-full transition-all relative " +
                    (schedule[day].available ? "bg-mouna-blue" : "bg-gray-200")}
                >
                  <div className={"w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all shadow " +
                    (schedule[day].available ? "right-0.5" : "left-0.5")}/>
                </button>
                <span className="font-bold text-navy">{day}</span>
              </div>
              {schedule[day].available && (
                <span className="text-xs text-teal font-bold">Available</span>
              )}
              {!schedule[day].available && (
                <span className="text-xs text-gray-400">Unavailable</span>
              )}
            </div>

            {schedule[day].available && (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-xs text-gray-400 mb-1">From</div>
                  <select
                    value={schedule[day].start}
                    onChange={e => updateTime(day, "start", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue bg-gray-50"
                  >
                    {HOURS.map(h => <option key={h}>{h}</option>)}
                  </select>
                </div>
                <div className="text-gray-400 mt-4">→</div>
                <div className="flex-1">
                  <div className="text-xs text-gray-400 mb-1">To</div>
                  <select
                    value={schedule[day].end}
                    onChange={e => updateTime(day, "end", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue bg-gray-50"
                  >
                    {HOURS.map(h => <option key={h}>{h}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        ))}

        <button onClick={handleSave} disabled={saving}
          className="w-full bg-mouna-blue text-white rounded-full py-4 font-bold text-sm mt-2">
          {saving ? "Saving..." : "Save Availability"}
        </button>
      </div>
    </div>
  );
}
